/**
 * React hook for loading event photos from IndexedDB.
 *
 * Returns an array of object URLs for all photos tagged to a given
 * suggestion ID. Handles the full lifecycle: load on mount, revoke
 * URLs on unmount, refresh after adds/deletes.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  addEventPhoto,
  deleteEventPhoto,
  getEventPhotoBlob,
  getEventPhotoKeys,
} from "./eventPhotos";

interface EventPhoto {
  key: string;
  url: string;
}

interface UseEventPhotosResult {
  photos: EventPhoto[];
  isLoading: boolean;
  addPhoto: (blob: Blob) => Promise<void>;
  removePhoto: (key: string) => Promise<void>;
  refresh: () => void;
}

export function useEventPhotos(
  suggestionId: string | null | undefined,
): UseEventPhotosResult {
  const [photos, setPhotos] = useState<EventPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    if (suggestionId === null || suggestionId === undefined) {
      setPhotos([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    async function load() {
      const keys = await getEventPhotoKeys(suggestionId!);
      const loaded: EventPhoto[] = [];
      for (const key of keys) {
        const blob = await getEventPhotoBlob(key);
        if (blob !== null && !cancelled) {
          const url = URL.createObjectURL(blob);
          loaded.push({ key, url });
        }
      }
      if (cancelled) {
        loaded.forEach((p) => URL.revokeObjectURL(p.url));
        return;
      }
      // Revoke previous URLs
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = loaded.map((p) => p.url);
      setPhotos(loaded);
      setIsLoading(false);
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [suggestionId, tick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    };
  }, []);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  const addPhotoFn = useCallback(
    async (blob: Blob) => {
      if (typeof suggestionId !== "string") return;
      await addEventPhoto(suggestionId, blob);
      refresh();
    },
    [suggestionId, refresh],
  );

  const removePhotoFn = useCallback(
    async (key: string) => {
      await deleteEventPhoto(key);
      refresh();
    },
    [refresh],
  );

  return {
    photos,
    isLoading,
    addPhoto: addPhotoFn,
    removePhoto: removePhotoFn,
    refresh,
  };
}
